import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { localizedRoutes } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

const products = [
  {
    id: 'mortadella',
    image: '/images/Mortadella_Suprema-1850_FIORUCCI_421X421_THUMB.jpg',
    titleKey: 'home.featuredProducts.mortadella.title',
    descriptionKey: 'home.featuredProducts.mortadella.description',
    badge: 'Best Seller',
    color: '#DF631E',
  },
  {
    id: 'prosciutto',
    image: '/images/Cotto_Fiordicotto_FIORUCCI_421X421_THUMB.jpg',
    titleKey: 'home.featuredProducts.prosciutto.title',
    descriptionKey: 'home.featuredProducts.prosciutto.description',
    badge: null,
    color: '#4E2118',
  },
  {
    id: 'guanciale',
    image: '/images/Anteprima-Homepage-Guanciale.jpg',
    titleKey: 'home.featuredProducts.guanciale.title',
    descriptionKey: 'home.featuredProducts.guanciale.description',
    badge: 'Tradizione',
    color: '#5C1A1A',
  },
  {
    id: 'affettati',
    image: '/images/affettati.png',
    titleKey: 'home.featuredProducts.affettati.title',
    descriptionKey: 'home.featuredProducts.affettati.description',
    badge: 'Novità',
    color: '#00441C',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
}

export function FeaturedProducts() {
  const { t, i18n } = useTranslation('common')
  const lang = i18n.language as SupportedLanguage
  const productsPath = localizedRoutes.products[lang]

  return (
    <section className="section-padding bg-[#FDF8F3] relative" aria-labelledby="featured-products-title">
      <div className="container-fiorucci">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#DF631E]/10 text-[#DF631E] text-sm font-semibold rounded-full mb-4">
            {t('nav.products')}
          </span>
          <h2 id="featured-products-title" className="heading-section text-[#4E2118] mb-4">
            {t('home.featuredProducts.title')}
          </h2>
          <p className="text-body-lg text-[#4E2118]/70 max-w-2xl mx-auto">
            {t('home.featuredProducts.subtitle')}
          </p>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {products.map((product) => (
            <motion.div key={product.id} variants={itemVariants}>
              <Link
                to={`/${lang}/${productsPath}/${product.id}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="group block h-full"
              >
                <article className="card-fiorucci h-full flex flex-col bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#F3E4D6]">
                    <img
                      src={product.image}
                      alt={t(product.titleKey)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    {/* Badge */}
                    {product.badge && (
                      <div
                        className="absolute top-4 left-4 px-3 py-1 text-xs font-bold text-white rounded-full"
                        style={{ backgroundColor: product.color }}
                      >
                        {product.badge}
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {/* Gluten-free badge */}
                    {product.id === 'mortadella' && (
                      <div className="absolute bottom-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md">
                        <img
                          src="/images/senza-glutine.svg"
                          alt="Senza Glutine"
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-6 flex flex-col">
                    <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-2 group-hover:text-[#DF631E] transition-colors">
                      {t(product.titleKey)}
                    </h3>
                    <p className="text-sm text-[#4E2118]/70 leading-relaxed mb-4 flex-1">
                      {t(product.descriptionKey)}
                    </p>
                    <div className="flex items-center gap-2 text-[#DF631E] font-semibold text-sm group-hover:gap-3 transition-all">
                      <span>{t('home.featuredProducts.discover')}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </article>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <LocalizedLink
            to="products"
            className="btn-secondary inline-flex"
          >
            {t('buttons.discover')} {t('nav.products')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </LocalizedLink>
        </motion.div>
      </div>
    </section>
  )
}
